// frontend/packages/shared/services/src/apiService.js
// Debug/trace logging: verbose axios request/response interceptors are enabled to surface
// API paths, payloads, and responses during media/logo/avatar debugging. Remove or reduce
// these logs once investigations are complete.
import axios from "axios";

/* ------------------------ Base URL normalization ------------------------ */
function readRuntimeApiBase() {
  const w = typeof window !== "undefined" ? window : {};
  const fromWindow =
    (w.__UPRYZZE_ENV__ && w.__UPRYZZE_ENV__.REACT_APP_API_URL) ||
    (w.__ENV__ && w.__ENV__.REACT_APP_API_URL) ||
    w.REACT_APP_API_URL ||
    w.API_BASE ||
    "";
  const fromBuild = (process.env.REACT_APP_API_URL || "").trim();
  const legacy = fromBuild || (process.env.API_BASE || "").trim();
  return String(fromWindow || legacy).trim();
}

function joinApi(base, path) {
  const b = (base || "").replace(/\/+$/, "");
  const p = (path || "").replace(/^\/+/, "");
  if (!b) {
    return `/api/${p}`.replace(/\/+$/, "");
  }
  if (/\/api$/i.test(b)) {
    return `${b}/${p}`.replace(/\/+$/, "");
  }
  if (/^api(\/|$)/i.test(p)) {
    return `${b}/${p}`.replace(/\/+$/, "");
  }
  return `${b}/api/${p}`.replace(/\/+$/, "");
}

function ensureAbsoluteRoot(baseCandidate) {
  const candidate = String(baseCandidate || "").trim();
  if (!candidate) {
    if (typeof window !== "undefined" && window.location?.origin) {
      return window.location.origin;
    }
    return "http://localhost:5000";
  }

  if (/^https?:\/\//i.test(candidate)) {
    return candidate.replace(/\/+$/, "");
  }

  if (candidate.startsWith("//")) {
    return `https:${candidate}`.replace(/\/+$/, "");
  }

  if (candidate.startsWith("/")) {
    if (typeof window !== "undefined" && window.location?.origin) {
      return `${window.location.origin}${candidate}`.replace(/\/+$/, "");
    }
    return `http://localhost:5000${candidate}`.replace(/\/+$/, "");
  }

  return `http://${candidate}`.replace(/\/+$/, "");
}

function stripTrailingApiSegment(url) {
  return url.replace(/\/api$/i, "");
}

function stripPath(url) {
  if (!url) return url;
  try {
    const parsed = new URL(url);
    return parsed.origin;
  } catch (err) {
    const withoutApi = String(url).split(/\/api\b/i)[0];
    const cleaned = withoutApi.replace(/\/.*$/, "");
    return cleaned || url;
  }
}

const runtimeBase = readRuntimeApiBase();
const absoluteRoot = ensureAbsoluteRoot(runtimeBase);
const rootWithoutPath = stripPath(absoluteRoot);
let ROOT = stripTrailingApiSegment(rootWithoutPath);
if (!ROOT) {
  ROOT = ensureAbsoluteRoot("");
}
const API_BASE = joinApi(ROOT, "").replace(/\/+$/, "");

if (process.env.NODE_ENV !== "production") {
  console.info("[apiService DEBUG] ROOT =", ROOT);
  console.info("[apiService DEBUG] API_BASE =", API_BASE);
}

/* --------------------------- Token helpers ----------------------------- */
const TOKEN_KEYS = ["token", "upryzze_token"];
const REFRESH_TOKEN_KEYS = ["refreshToken", "upryzze_refresh_token"];

function readToken() {
  for (const k of TOKEN_KEYS) {
    const v = sessionStorage.getItem(k) || localStorage.getItem(k);
    if (v) return v;
  }
  return null;
}

function persistToken(token) {
  TOKEN_KEYS.forEach((k) => {
    try {
      sessionStorage.setItem(k, token);
    } catch {}
  });
}

function readRefreshToken() {
  for (const k of REFRESH_TOKEN_KEYS) {
    const v = sessionStorage.getItem(k) || localStorage.getItem(k);
    if (v) return v;
  }
  return null;
}

function persistRefreshToken(token) {
  REFRESH_TOKEN_KEYS.forEach((k) => {
    try {
      sessionStorage.setItem(k, token);
    } catch {}
  });
}

/* ---- App-supplied silent token refresher --------------------------------
 * The backend has NO /api/auth/refresh endpoint (CIAM/MSAL issues tokens).
 * The Console registers a refresher here (MSAL acquireTokenSilent via
 * AuthContext) so an expired access token is silently re-acquired and the
 * failed request retried, instead of every 401 bouncing the user to /login
 * mid-session. When unset, refreshAccessToken falls back to the legacy POST. */
let externalTokenRefresher = null;
export function setApiTokenRefresher(fn) {
  externalTokenRefresher = typeof fn === "function" ? fn : null;
}

function persistUser(user) {
  if (!user) return;
  try {
    sessionStorage.setItem("user", JSON.stringify(user));
    if (user?.featureFlags) {
      sessionStorage.setItem("featureFlags", JSON.stringify(user.featureFlags));
    }
    if (user?.businessAccess) {
      sessionStorage.setItem(
        "businessAccess",
        JSON.stringify(user.businessAccess),
      );
    }
  } catch {}
}

function clearStoredAuth() {
  try {
    // Only clear our app keys — do not wipe all sessionStorage (MSAL lives there)
    for (const k of [
      ...TOKEN_KEYS,
      ...REFRESH_TOKEN_KEYS,
      "activeBusinessId",
      "availableBusinesses",
      "user",
      "featureFlags",
      "businessAccess",
    ]) {
      try {
        sessionStorage.removeItem(k);
      } catch {}
      try {
        localStorage.removeItem(k);
      } catch {}
    }
  } catch {}
}

/* ---------------------------- Axios clients ---------------------------- */
export const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
  withCredentials: false,
  timeout: 30000,
});

export const authApi = axios.create({
  baseURL: ROOT,
  headers: { "Content-Type": "application/json" },
  withCredentials: true,
  timeout: 30000,
});

const attachLoggingInterceptors = (instance, label = "api") => {
  instance.interceptors.request.use(
    (config) => {
      const loggableHeaders = { ...(config.headers || {}) };
      delete loggableHeaders.Authorization;
      console.log("[API][Request]", {
        client: label,
        method: config.method,
        url: config.url,
        baseURL: config.baseURL,
        params: config.params,
        data: config.data,
        headers: loggableHeaders,
      });
      return config;
    },
    (error) => {
      console.error("[API][Request][Error]", error);
      return Promise.reject(error);
    },
  );
  instance.interceptors.response.use(
    (response) => {
      console.log("[API][Response]", {
        client: label,
        url: response?.config?.url,
        status: response?.status,
        data: response?.data,
      });
      return response;
    },
    (error) => {
      if (error?.response) {
        console.error("[API][Response][Error]", {
          client: label,
          url: error?.config?.url,
          status: error?.response?.status,
          data: error?.response?.data,
        });
      } else {
        console.error("[API][Network][Error]", error?.message || error);
      }
      return Promise.reject(error);
    },
  );
};

/* ------------------------ Request path utilities ----------------------- */
const TENANT_HEADER = "x-business-id";
const TENANT_REGEX = /^[a-f0-9]{24}$/i;

function resolvePath(cfg) {
  try {
    const u = new URL(cfg.url, cfg.baseURL || ROOT);
    return u.pathname || "";
  } catch {
    try {
      return String(cfg.url || "").split("?")[0];
    } catch {
      return "";
    }
  }
}

function shouldAttachTenantHeader(pathname) {
  if (!pathname) return false;
  // Never send tenant header to auth/public/health – bootstrap must be clean
  if (/^\/?(api\/)?auth(\/|$)/i.test(pathname)) return false;
  if (/^\/?(api\/)?public(\/|$)/i.test(pathname)) return false;
  if (/^\/?(api\/)?health(\/|$)/i.test(pathname)) return false;
  return true;
}

function tenantFromUrl() {
  try {
    const m = window.location.pathname.match(/\/business\/([a-f0-9]{24})/i);
    return m ? m[1] : null;
  } catch {
    return null;
  }
}

function tenantFromSelection() {
  try {
    return (
      sessionStorage.getItem("activeBusinessId") ||
      localStorage.getItem("activeBusinessId") ||
      null
    );
  } catch {
    return null;
  }
}

function tenantFromUser() {
  try {
    const selected = tenantFromSelection();
    if (selected) return selected;
    const raw = sessionStorage.getItem("user") || localStorage.getItem("user");
    if (!raw) return null;
    const u = JSON.parse(raw);
    return (
      u.activeBusinessId ||
      u.businessId ||
      (Array.isArray(u.businessAccess) && u.businessAccess[0]?.businessId) ||
      null
    );
  } catch {
    return null;
  }
}

/* ----------------------------- Interceptors ---------------------------- */
const attachAuthInterceptors = (instance) => {
  const shouldAttemptRefresh = (cfg) => {
    const path = resolvePath(cfg);
    if (!path) return false;
    if (/\/auth\/refresh$/i.test(path)) return false;
    if (/\/auth\/login$/i.test(path) || /\/auth\/register$/i.test(path))
      return false;
    if (/\/auth\/azure-login$/i.test(path)) return false;
    return true;
  };

  let refreshPromise = null;

  const refreshAccessToken = async () => {
    if (refreshPromise) return refreshPromise;
    refreshPromise = (async () => {
      // Prefer the app-supplied silent refresher (Console: MSAL
      // acquireTokenSilent). It owns refresh end to end, so we do NOT fall
      // through to the legacy POST when it is set; a null return means the
      // session genuinely can't be refreshed and the caller logs out.
      if (externalTokenRefresher) {
        let next = null;
        try {
          next = await externalTokenRefresher();
        } catch {
          next = null;
        }
        if (next) persistToken(next);
        return next || null;
      }

      const payload = {};
      const storedRefresh = readRefreshToken();
      if (storedRefresh) payload.refreshToken = storedRefresh;

      const { data } = await authApi.post("/api/auth/refresh", payload);
      if (data?.token) {
        persistToken(data.token);
      }
      if (data?.refreshToken) {
        persistRefreshToken(data.refreshToken);
      }
      if (data?.user) {
        persistUser(data.user);
      }
      return data?.token || null;
    })();

    try {
      return await refreshPromise;
    } finally {
      refreshPromise = null;
    }
  };

  const handleAuthFailure = (status) => {
    clearStoredAuth();
    try {
      if (typeof window !== "undefined") {
        window.sessionStorage.setItem(
          "postLoginRedirect",
          window.location?.href || "/dashboard",
        );
      }
    } catch {}
    if ((status === 401 || status === 403) && typeof window !== "undefined") {
      if (window?.location?.pathname !== "/login")
        window.location.href = "/login";
    }
  };

  instance.interceptors.request.use((cfg) => {
    const path = resolvePath(cfg);

    // Authorization
    if (/\/api\/auth\/refresh$/i.test(path)) {
      delete cfg.headers.Authorization;
    } else {
      const token = readToken();
      if (token) cfg.headers.Authorization = `Bearer ${token}`;
      else delete cfg.headers.Authorization;
    }

    // x-business-id (strict)
    if (shouldAttachTenantHeader(path)) {
      const suppliedHeader = cfg?.headers?.[TENANT_HEADER];
      const candidate = suppliedHeader || tenantFromUrl() || tenantFromUser();

      if (candidate && TENANT_REGEX.test(candidate)) {
        cfg.headers[TENANT_HEADER] = candidate; // allow explicit overrides per request
      } else {
        delete cfg.headers[TENANT_HEADER]; // never send junk
      }
    } else {
      delete cfg.headers[TENANT_HEADER]; // never attach to auth/public/health
    }

    return cfg;
  });

  instance.interceptors.response.use(
    (res) => res,
    async (err) => {
      const status = err?.response?.status;
      const msg = (err?.response?.data?.message || "").toString();
      const config = err?.config || {};
      const isNetErr = !!err?.code && !status;
      const retriable =
        isNetErr || status === 502 || status === 503 || status === 504;

      // One retry on transient failures
      if (retriable && !config.__retried) {
        config.__retried = true;
        return instance.request(config);
      }

      // If we get a tenant access 403, try to route the user to their active tenant UI once
      if (
        status === 403 &&
        /requested business/i.test(msg) &&
        !config.__tenantRedirected
      ) {
        config.__tenantRedirected = true;
        const active = tenantFromUser();
        if (active && typeof window !== "undefined") {
          const curMatch = window.location.pathname.match(
            /\/business\/([a-f0-9]{24})/i,
          );
          if (!curMatch || curMatch[1] !== active) {
            window.location.replace(`/business/${active}/dashboard`);
            return Promise.reject(err);
          }
        }
      }

      const authExpired =
        status === 401 ||
        (status === 403 &&
          /token|expired|authorization|unauthorized/i.test(msg));

      if (authExpired) {
        const eligibleForRefresh =
          shouldAttemptRefresh(config) && !config.__refreshTried;
        if (eligibleForRefresh) {
          config.__refreshTried = true;
          try {
            const nextToken = await refreshAccessToken();
            if (nextToken) {
              config.headers.Authorization = `Bearer ${nextToken}`;
              return instance.request(config);
            }
          } catch (refreshError) {
            handleAuthFailure(status);
            return Promise.reject(refreshError);
          }
        }

        handleAuthFailure(status);
      }
      return Promise.reject(err);
    },
  );
};

attachLoggingInterceptors(api, "api");
attachLoggingInterceptors(authApi, "authApi");
// Attach to both clients
attachAuthInterceptors(api);
attachAuthInterceptors(authApi);

/* ------------------------------- Helpers -------------------------------- */

// Base URL the `api` axios client posts against (already includes `/api`).
// Exposed so streaming callers using raw `fetch` (EventSource can't send an
// Authorization header — F.1) hit the exact same origin/path as axios.
export function getApiBase() {
  return API_BASE;
}

// Reproduce the request interceptor's auth + tenant header logic for a raw
// `fetch` call. Same token source (readToken) and same strict x-business-id
// resolution as axios, so a streaming request authenticates identically.
// `pathname` is the request path (used only for the tenant-header allowlist).
export function buildTenantAuthHeaders(pathname = "") {
  const headers = {};
  const token = readToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (shouldAttachTenantHeader(pathname)) {
    const candidate = tenantFromUrl() || tenantFromUser();
    if (candidate && TENANT_REGEX.test(candidate)) {
      headers[TENANT_HEADER] = candidate;
    }
  }
  return headers;
}

export default api;
